import {
  Box,
  BoxProps,
  Button as ChakraButton,
  ButtonGroup as ChakraButtonGroup,
  Flex,
  Icon,
  IconButton as ChakraIconButton,
  IconButtonProps,
  Tag,
  TagCloseButton,
  TagLabel,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { EllipsisHorizontalIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Card } from '@main/complykit/components/card';
import { Content } from '@main/complykit/components/slots';
import { Text } from '@main/complykit/components/text';
import { useStableCallback } from '@main/shared/browser';
import { isNonNullable } from '@main/shared/utils';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOverflowItems, useRemainingSpace } from '../overflow';
import { BulkAction, BulkActionButtons, WrappedBulkActionState } from './bulk-action-buttons';
/* The frozen Chakra engine, not the ComplyKit one: the field menu hosts its
 * editors through wrapper(), which the ComplyKit Menu cannot seat (see
 * chakra-menu-actions.tsx). */
import {
  ChakraMenuActionItem,
  MenuActions,
  MenuActionsProps,
  TriggerButtonProps,
} from './chakra-menu-actions';

export interface BulkActionBarProps {
  name?: string;
  selectionSize: number;
  totalSize?: number;
  primaryActions?: BulkAction<WrappedBulkActionState>[];
  secondaryActions?: BulkAction[];
  cardProps?: Omit<BoxProps, 'children'>;
  primaryMenuActionProps?: Omit<MenuActionsProps, 'actions' | 'renderTrigger'>;
  secondaryMenuActionProps?: Omit<MenuActionsProps, 'actions' | 'renderTrigger'>;
  onSelectAll?(): void;
  onDeselectAll?(): void;
}

const EMPTY_ARRAY: never[] = [];

/* Parked with the Table round: the bulk-action bar is table engine chrome
 * (its overflow measuring and menu trigger convert with the engine,
 * component-map.md). */
export function BulkActionBar(props: PropsWithChildren<BulkActionBarProps>) {
  const { t } = useTranslation('ui');
  const { containerRef, visibilityMap } = useOverflowItems();
  const remainingSpace = useRemainingSpace(containerRef);
  const [isFieldMenuOpen, setIsFieldMenuOpen] = useState(false);
  const [shouldCloseFieldMenu, setShouldCloseFieldMenu] = useState(false);
  const { onBulkEditorOpenChange, activeItems, hasActiveEditors } = useActiveEditors();
  const state: WrappedBulkActionState = {
    isVisible: true,
    onBulkEditorOpenChange,
  };

  useEffect(() => {
    if (activeItems === 0 && shouldCloseFieldMenu) {
      setShouldCloseFieldMenu(false);
      setIsFieldMenuOpen(false);
    }
  }, [activeItems, shouldCloseFieldMenu]);

  const [hasVisibleActions, hasHiddenActions] = useMemo(() => {
    const visibilityValues = Object.values(visibilityMap);
    return [visibilityValues.some((v) => v), visibilityValues.some((v) => !v)];
  }, [visibilityMap]);

  const primaryActions = useMemo(
    () =>
      (props.primaryActions?.map((action, i, arr) => ({
        ...action,
        buttonProps: {
          'data-targetid': getActionId(action),
          visibility: visibilityMap[getActionId(action)] ? 'visible' : 'hidden',
          flexShrink: 0,
          borderEndRadius: hasHiddenActions && i === arr.length - 1 ? 0 : undefined,
          ...action.buttonProps,
        },
      })) as BulkAction<WrappedBulkActionState>[]) ?? EMPTY_ARRAY,
    [hasHiddenActions, props.primaryActions, visibilityMap],
  );

  const primaryHiddenActions = useMemo(
    () => primaryActions.filter((action) => !visibilityMap[getActionId(action)]),
    [primaryActions, visibilityMap],
  );

  const [secondaryVisibleAction, ...secondaryHiddenActions] = useMemo(
    () => props.secondaryActions ?? EMPTY_ARRAY,
    [props.secondaryActions],
  );

  function getMenuTrigger(externalProps?: Partial<IconButtonProps>) {
    return (props: TriggerButtonProps) => (
      <Tooltip label={t('bulkActions.moreActions')} placement="top" hasArrow>
        <ChakraIconButton
          {...props}
          size="sm"
          variant="outline"
          icon={<Icon as={EllipsisVerticalIcon} boxSize="4" />}
          aria-label={t('bulkActions.moreActions')}
          {...externalProps}
        />
      </Tooltip>
    );
  }

  return (
    <Box
      position="sticky"
      bottom="0"
      zIndex="sticky"
      aria-label={`Bulk actions bar ${props.name}`}
      {...props.cardProps}
    >
      <Card variant="elevated" padding="none">
        <Content>
          <Flex direction="row" gap="2" px="4" py="3.5" color="chakra-body-text">
            <Tag
              size="sm"
              variant="outline"
              border="1px solid"
              borderColor={useColorModeValue('gray.200', 'whiteAlpha.300')}
              boxShadow="none"
              overflow="visible"
              flexShrink={0}
              h={8}
            >
              <TagLabel color="chakra-body-text" fontSize="sm">
                <Text as="span" size="sm">
                  {props.selectionSize}
                </Text>{' '}
                <Text as="span" size="sm">
                  {t('bulkActions.selectedNo')}
                </Text>
              </TagLabel>
              {props.onDeselectAll && props.selectionSize ? (
                <TagCloseButton color="chakra-body-text" onClick={props.onDeselectAll} />
              ) : undefined}
            </Tag>
            {props.onSelectAll && (
              <ChakraButton
                size="sm"
                variant="outline"
                colorScheme="blue"
                fontWeight="normal"
                isDisabled={props.selectionSize === props.totalSize}
                onClick={props.onSelectAll}
              >
                {t('bulkActions.selectAll')}
              </ChakraButton>
            )}
            <Flex flex="1" justifyContent="flex-end" position="relative" overflow="hidden">
              <ChakraButtonGroup
                size="sm"
                variant="outline"
                overflow="hidden"
                isAttached
                aria-label="Primary bulk actions"
              >
                <BulkActionButtons
                  actions={primaryActions}
                  state={state}
                  buttonGroupProps={{
                    position: 'relative',
                    overflow: 'hidden',
                    left: primaryHiddenActions.length > 0 ? `${Math.ceil(remainingSpace)}px` : 0,
                  }}
                  ref={containerRef}
                ></BulkActionButtons>
                {primaryHiddenActions.length > 0 && (
                  <MenuActions
                    actions={primaryHiddenActions as ChakraMenuActionItem<WrappedBulkActionState>[]}
                    extra={{ ...state, isVisible: false }}
                    renderTrigger={getMenuTrigger({
                      icon: <Icon as={EllipsisHorizontalIcon} boxSize="4" />,
                      borderStartRadius: hasVisibleActions ? 0 : undefined,
                      borderLeftWidth: hasVisibleActions ? 0 : undefined,
                    })}
                    closeOnBlur={activeItems === 0}
                    isOpen={isFieldMenuOpen}
                    placement="auto-end"
                    menuListProps={{
                      maxH: 'calc(100vh - 6rem)',
                      overflow: 'hidden',
                    }}
                    menuItemsProps={{
                      maxH: 'calc(100vh - 6rem)',
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                    }}
                    onOpen={() => {
                      setShouldCloseFieldMenu(false);
                      setIsFieldMenuOpen(true);
                    }}
                    onClose={() => {
                      if (hasActiveEditors()) {
                        setShouldCloseFieldMenu(true);
                        return;
                      }

                      setShouldCloseFieldMenu(false);
                      setIsFieldMenuOpen(false);
                    }}
                    {...props.primaryMenuActionProps}
                  />
                )}
              </ChakraButtonGroup>
            </Flex>
            <Flex direction="row" gap="2" aria-label="Secondary bulk actions">
              <BulkActionButtons actions={[secondaryVisibleAction].filter(isNonNullable)} />
              <MenuActions
                actions={secondaryHiddenActions as ChakraMenuActionItem[]}
                renderTrigger={getMenuTrigger()}
                closeOnBlur
                {...props.secondaryMenuActionProps}
              />
            </Flex>
          </Flex>
        </Content>
      </Card>
    </Box>
  );
}

function getActionId(action: BulkAction<unknown>) {
  return action.id ?? action.label;
}

type OnBulkEditorOpenChange = (id: string, isOpen: boolean) => void;

const useActiveEditors = () => {
  const openEditors = useRef(new Set<string>());
  const [activeItems, setActiveItems] = useState(0);

  const onBulkEditorOpenChange = useStableCallback<OnBulkEditorOpenChange>((id, isOpen) => {
    if (isOpen) {
      openEditors.current.add(id);
    } else {
      openEditors.current.delete(id);
    }
    setActiveItems(openEditors.current.size);
  });

  const hasActiveEditors = useStableCallback(() => openEditors.current.size > 0);

  return useMemo(
    () => ({
      onBulkEditorOpenChange,
      activeItems,
      hasActiveEditors,
    }),
    [onBulkEditorOpenChange, activeItems, hasActiveEditors],
  );
};
